import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import logo from '../img/ICETEX-TRANSPARENTE.png';

// Link de navegación GOV.CO: activo = Cobalt con subrayado; inactivo = Cobalt sólido con hover.
// Sin opacidad en el color del texto (la cobalt al 70% falla AA sobre blanco); el estado activo se distingue por subrayado.
const NAV_LINK = (isActive) =>
  `relative no-underline font-semibold text-[15px] whitespace-nowrap transition-colors duration-300 pb-1 ${
    isActive ? 'text-cobalt border-b-2 border-cobalt' : 'text-cobalt hover:text-cobalt-hover'
  }`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isDiagnosticMenuOpen, setIsDiagnosticMenuOpen] = useState(false);
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const userMenuRef = useRef(null);
  const diagnosticMenuRef = useRef(null);

  const isActive = (path) => location.pathname === path;

  // Cerrar los menús al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (diagnosticMenuRef.current && !diagnosticMenuRef.current.contains(event.target)) {
        setIsDiagnosticMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    setIsMenuOpen(false);
    navigate('/');
  };

  const handleMenuLinkClick = () => {
    setIsMenuOpen(false);
    setIsDiagnosticMenuOpen(false);
    setIsUserMenuOpen(false);
  };

  return (
    <header className="bg-white w-full sticky top-0 z-[1000] shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 lg:px-10 h-[72px]">
        <Link to="/" onClick={handleMenuLinkClick} className="flex-shrink-0">
          <img src={logo} alt="Logo ICETEX" className="h-[50px] object-contain" />
        </Link>

        {/* Hamburguesa (mobile) */}
        <button
          className="hidden max-nav:flex flex-col justify-center items-center gap-[5px] bg-transparent border-0 p-2 cursor-pointer w-10 h-10"
          aria-expanded={isMenuOpen}
          aria-label="Menú de navegación"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <span className={`block w-6 h-[2px] bg-cobalt transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
          <span className={`block w-6 h-[2px] bg-cobalt transition-all duration-300 ${isMenuOpen ? 'opacity-0' : ''}`} />
          <span className={`block w-6 h-[2px] bg-cobalt transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
        </button>

        <nav className={`${isMenuOpen
          ? 'flex max-nav:absolute max-nav:left-0 max-nav:right-0 max-nav:top-full max-nav:bg-white max-nav:shadow-[0_4px_12px_rgba(0,0,0,0.08)] max-nav:border-t max-nav:border-gray-border max-nav:py-4 max-nav:px-6 max-nav:flex-col'
          : 'max-nav:hidden'
        } nav:flex nav:items-center nav:gap-7`}>

          <div className="flex items-center gap-7 max-nav:flex-col max-nav:items-start max-nav:gap-4">
            <Link to="/" className={NAV_LINK(isActive('/'))} onClick={handleMenuLinkClick}>
              Inicio
            </Link>

            {/* Diagnósticos con dropdown */}
            <div className="relative max-nav:w-full" ref={diagnosticMenuRef}>
              <button
                className={`inline-flex items-center gap-[6px] bg-transparent border-0 p-0 cursor-pointer ${NAV_LINK(isActive('/diagnosticos') || isActive('/resultados'))}`}
                onClick={() => setIsDiagnosticMenuOpen(!isDiagnosticMenuOpen)}
                aria-expanded={isDiagnosticMenuOpen}
              >
                <span>Diagnósticos</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`transition-transform duration-300 ${isDiagnosticMenuOpen ? 'rotate-180' : ''}`}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>

              {isDiagnosticMenuOpen && (
                <div className="absolute bg-white overflow-hidden z-[1000] top-[calc(100%+12px)] left-0 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.12)] border border-gray-border min-w-[250px] animate-dropdown max-nav:static max-nav:shadow-none max-nav:border-0 max-nav:rounded-none max-nav:mt-2 max-nav:min-w-0">
                  <Link
                    to="/diagnosticos"
                    className="flex items-center bg-transparent border-0 w-full text-left cursor-pointer font-semibold no-underline gap-3 px-5 py-[14px] text-[#1a202c] text-[15px] border-b border-gray-border transition-all duration-200 last:border-b-0 hover:bg-[#f7fafc] hover:pl-[25px] hover:text-cobalt"
                    onClick={handleMenuLinkClick}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="12" y1="18" x2="12" y2="12"></line>
                      <line x1="9" y1="15" x2="15" y2="15"></line>
                    </svg>
                    Realizar Diagnóstico
                  </Link>
                  {user && (
                    <Link
                      to="/resultados"
                      className="flex items-center bg-transparent border-0 w-full text-left cursor-pointer font-semibold no-underline gap-3 px-5 py-[14px] text-[#1a202c] text-[15px] border-b border-gray-border transition-all duration-200 last:border-b-0 hover:bg-[#f7fafc] hover:pl-[25px] hover:text-cobalt"
                      onClick={handleMenuLinkClick}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10 9 9 9 8 9"></polyline>
                      </svg>
                      Ver Mis Resultados
                    </Link>
                  )}
                </div>
              )}
            </div>

            <a href="https://moodle.cursosvirtualesicetex.com//login/index.php" target="_blank" rel="noopener noreferrer" className={NAV_LINK(false)} onClick={handleMenuLinkClick}>Formación</a>
            <Link to="/recursos" className={NAV_LINK(isActive('/recursos'))} onClick={handleMenuLinkClick}>Recursos</Link>
          </div>

          <div className="hidden nav:block w-px h-6 bg-gray-border" />

          {/* Zona de autenticación — botones según variantes del manual (sección 5.2) */}
          <div className="flex items-center gap-3 max-nav:flex-col max-nav:items-stretch max-nav:w-full max-nav:mt-4 max-nav:pt-4 max-nav:border-t max-nav:border-gray-border">
            {user ? (
              <div className="relative max-nav:w-full" ref={userMenuRef}>
                <button
                  className="inline-flex items-center justify-center gap-2 border-[1.5px] border-cobalt text-cobalt py-[8px] px-5 rounded text-sm font-bold bg-transparent cursor-pointer whitespace-nowrap transition-all duration-300 hover:bg-cobalt hover:text-white max-nav:w-full"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  aria-expanded={isUserMenuOpen}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  <span>{user.full_name}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`transition-transform duration-300 ${isUserMenuOpen ? 'rotate-180' : ''}`}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>

                {isUserMenuOpen && (
                  <div className="absolute bg-white overflow-hidden z-[1000] top-[calc(100%+12px)] right-0 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.12)] border border-gray-border min-w-[220px] animate-dropdown max-nav:static max-nav:shadow-none max-nav:border-0 max-nav:rounded-none max-nav:mt-3 max-nav:min-w-0">
                    <Link
                      to="/change-password"
                      className="flex items-center bg-transparent border-0 w-full text-left cursor-pointer font-semibold no-underline gap-3 px-5 py-[14px] text-[#1a202c] text-[15px] border-b border-gray-border transition-all duration-200 last:border-b-0 hover:bg-[#f7fafc] hover:pl-[25px] hover:text-cobalt"
                      onClick={handleMenuLinkClick}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                      </svg>
                      Cambiar Contraseña
                    </Link>
                    <button
                      className="flex items-center bg-transparent border-0 w-full text-left cursor-pointer font-semibold no-underline gap-3 px-5 py-[14px] text-[#dc3545] text-[15px] border-b border-gray-border transition-all duration-200 last:border-b-0 hover:bg-[#fff5f5] hover:pl-[25px] hover:text-[#c82333]"
                      onClick={handleLogout}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#dc3545]">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                        <polyline points="16 17 21 12 16 7"></polyline>
                        <line x1="21" y1="12" x2="9" y2="12"></line>
                      </svg>
                      Cerrar Sesión
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="bg-cobalt text-white py-[9px] px-5 rounded text-sm font-bold no-underline whitespace-nowrap transition-all duration-300 hover:bg-cobalt-hover hover:shadow-md max-nav:w-full max-nav:text-center max-nav:py-3" onClick={handleMenuLinkClick}>
                Iniciar Sesión
              </Link>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
